import { Timestamp } from 'firebase/firestore';

// Tipos de alertas disponibles
export type AlertType =
  | 'price_greater_than'      // Precio > X
  | 'price_less_than'         // Precio < X
  | 'price_equals'            // Precio == X
  | 'price_vs_product'        // Precio A vs Producto B
  | 'distance_value_abs'      // |Precio - X| > threshold
  | 'distance_value_pct'      // |Precio - X| / X > threshold%
  | 'distance_product_abs'    // |Precio A - Precio B| > threshold
  | 'distance_product_pct';   // |Precio A - Precio B| / Precio B > threshold%

// Tipo de asignación de alerta
export type AlertAssignmentType = 'individual' | 'sku' | 'comparison_group';

// Operadores de comparación
export type ComparisonOperator = 'greater' | 'less' | 'equal';

// Configuración del trigger según tipo de alerta
export interface TriggerConfig {
  // Para price_greater_than, price_less_than, price_equals
  targetValue?: number;
  valueMode?: 'absolute' | 'percent';
  percentValue?: number;
  referencePrice?: number;

  // Para price_vs_product, distance_product_*
  referenceProductId?: string;
  referenceProductTitle?: string;
  referenceProductSitio?: string;
  comparisonOperator?: ComparisonOperator;

  // Para distance_*
  distanceValue?: number;
  distanceOperator?: ComparisonOperator;
}

// Configuración de notificación por email
export interface EmailNotificationConfig {
  enabled: boolean;
  recipients: string[];
}

// Información básica del producto guardada en la alerta
export interface AlertProductDetail {
  id_catalogo: string;
  title: string;
  sitio?: string;
  imagen?: string;
}

// Documento principal de alerta
export interface Alert {
  id?: string;
  clientId: string;
  name: string;
  description?: string;
  alertType: AlertType;
  triggerConfig: TriggerConfig;
  assignmentType: AlertAssignmentType;
  productIds?: string[];           // Para 'individual'
  productDetails?: AlertProductDetail[]; // Información de productos para mostrar
  skuCode?: string;                // Para 'sku' (dinámico)
  comparisonGroupId?: string;      // Para 'comparison_group'
  isActive: boolean;
  emailNotification: EmailNotificationConfig;
  createdBy: string;
  createdByName: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  isPublic: boolean;
}

// Producto que disparó una alerta
export interface TriggeredProduct {
  id_catalogo: string;
  title: string;
  sitio: string;
  currentPrice: number;
  previousPrice?: number;
  referencePrice?: number;
  triggerReason: string;
  imagen?: string;
}

// Documento de alerta disparada (historial)
export interface AlertTriggered {
  id?: string;
  alertId: string;
  clientId: string;
  tomaId: number;
  tomaDate: string;
  tableName: string;
  triggeredProducts: TriggeredProduct[];
  triggerCount: number;
  alertType: AlertType;
  alertName: string;
  emailSent: boolean;
  emailSentAt?: Timestamp;
  emailRecipients?: string[];
  triggeredAt: Timestamp;
}

// Historial de emails usados
export interface AlertEmailHistory {
  id?: string;
  clientId: string;
  email: string;
  lastUsedAt: Timestamp;
  usageCount: number;
}

// Información para crear/actualizar una alerta (sin campos auto-generados)
export interface AlertInput {
  name: string;
  description?: string;
  alertType: AlertType;
  triggerConfig: TriggerConfig;
  assignmentType: AlertAssignmentType;
  productIds?: string[];
  productDetails?: AlertProductDetail[]; // Información de productos para mostrar
  skuCode?: string;
  comparisonGroupId?: string;
  isActive: boolean;
  emailNotification: EmailNotificationConfig;
  isPublic: boolean;
}

// Producto básico para selección
export interface ProductForAlert {
  id_catalogo: string;
  title: string;
  sitio: string;
  sale_price?: string | number;
  original_price?: string | number;
  imagen?: string;
}

// Metadatos de tipos de alerta para UI
export interface AlertTypeInfo {
  type: AlertType;
  labelKey: string;           // Key de traducción
  descriptionKey: string;     // Key de traducción de descripción
  category: 'price' | 'comparison' | 'availability';
  requiresValue: boolean;
  requiresProduct: boolean;
  requiresDistance: boolean;
}

// Información de tipos de alerta
export const ALERT_TYPE_INFO: AlertTypeInfo[] = [
  {
    type: 'price_greater_than',
    labelKey: 'alerts.types.price_greater_than',
    descriptionKey: 'alerts.types.price_greater_than_desc',
    category: 'price',
    requiresValue: true,
    requiresProduct: false,
    requiresDistance: false
  },
  {
    type: 'price_less_than',
    labelKey: 'alerts.types.price_less_than',
    descriptionKey: 'alerts.types.price_less_than_desc',
    category: 'price',
    requiresValue: true,
    requiresProduct: false,
    requiresDistance: false
  },
  {
    type: 'price_equals',
    labelKey: 'alerts.types.price_equals',
    descriptionKey: 'alerts.types.price_equals_desc',
    category: 'price',
    requiresValue: true,
    requiresProduct: false,
    requiresDistance: false
  },
  {
    type: 'price_vs_product',
    labelKey: 'alerts.types.price_vs_product',
    descriptionKey: 'alerts.types.price_vs_product_desc',
    category: 'comparison',
    requiresValue: false,
    requiresProduct: true,
    requiresDistance: false
  },
  {
    type: 'distance_value_abs',
    labelKey: 'alerts.types.distance_value_abs',
    descriptionKey: 'alerts.types.distance_value_abs_desc',
    category: 'comparison',
    requiresValue: true,
    requiresProduct: false,
    requiresDistance: true
  },
  {
    type: 'distance_value_pct',
    labelKey: 'alerts.types.distance_value_pct',
    descriptionKey: 'alerts.types.distance_value_pct_desc',
    category: 'comparison',
    requiresValue: true,
    requiresProduct: false,
    requiresDistance: true
  },
  {
    type: 'distance_product_abs',
    labelKey: 'alerts.types.distance_product_abs',
    descriptionKey: 'alerts.types.distance_product_abs_desc',
    category: 'comparison',
    requiresValue: false,
    requiresProduct: true,
    requiresDistance: true
  },
  {
    type: 'distance_product_pct',
    labelKey: 'alerts.types.distance_product_pct',
    descriptionKey: 'alerts.types.distance_product_pct_desc',
    category: 'comparison',
    requiresValue: false,
    requiresProduct: true,
    requiresDistance: true
  }
];

// Helper para obtener info de un tipo de alerta
export function getAlertTypeInfo(type: AlertType): AlertTypeInfo | undefined {
  return ALERT_TYPE_INFO.find(info => info.type === type);
}

// Helper para agrupar tipos por categoría
export function getAlertTypesByCategory(): Record<string, AlertTypeInfo[]> {
  return ALERT_TYPE_INFO.reduce((acc, info) => {
    if (!acc[info.category]) {
      acc[info.category] = [];
    }
    acc[info.category].push(info);
    return acc;
  }, {} as Record<string, AlertTypeInfo[]>);
}
